'use client';

import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Capacitor } from '@capacitor/core';
import { useStory } from '../storyblok/hooks/useStoryblok';
import { SbButtonBlock, SbComponentBase, SbTextBlock } from '../storyblok/types';
import { typography } from '../../theme';
import { colors } from '../../theme/color';
import { Primary } from '../button';
import { ThreeDotsLoader } from '../loading/ThreeDotsLoader';
import { isDevMode } from '../../utils/isDevMode';

type ForcedUpdateData = SbComponentBase & {
  blocks: SbComponentBase[];
};

const getLinkUrl = (): string => {
  const currentPlatform = Capacitor.getPlatform();

  const devGooglePlayStoreLink =
    'https://play.google.com/store/apps/details?id=fi.kotipizzadev.app';
  const prodGooglePlayStoreLink = 'https://play.google.com/store/apps/details?id=fi.kotipizza.app';
  const devAppleAppStoreLink = 'https://apps.apple.com/app/kotipizza-dev/id6749227451';
  const prodAppleAppStoreLink = 'https://apps.apple.com/app/kotipizza/id6746118819';

  if (currentPlatform === 'android') {
    return isDevMode() ? devGooglePlayStoreLink : prodGooglePlayStoreLink;
  } else if (currentPlatform === 'ios') {
    return isDevMode() ? devAppleAppStoreLink : prodAppleAppStoreLink;
  } else {
    return prodAppleAppStoreLink;
  }
};

export const AppForcedUpdatePage: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useStory<ForcedUpdateData>(
    'app/paivita-sovellus',
    'contentPage'
  );

  if (isLoading) {
    return (
      <PageContainer>
        <StyledLogo src={theme?.logo.white} alt="Kotipizza" />
        <StyledVerticalCenteringContainer>
          <StyledContainer>
            <ThreeDotsLoader />
          </StyledContainer>
        </StyledVerticalCenteringContainer>
      </PageContainer>
    );
  }

  if (!data || error) {
    return (
      <PageContainer>
        <StyledLogo src={theme?.logo.white} alt="Kotipizza" />
        <StyledVerticalCenteringContainer>
          <StyledContainer>
            <StyledTitle>Aika päivittää</StyledTitle>
            <StyledText>
              Päivitä sovellus jatkaaksesi tilaamista ja etujen hyödyntämistä.
            </StyledText>
            <Primary to={getLinkUrl()} aria-label="Päivitä sovellus">
              Päivitä nyt
            </Primary>
          </StyledContainer>
        </StyledVerticalCenteringContainer>
      </PageContainer>
    );
  }

  const textBlock = data.blocks[0] as SbTextBlock;
  const linkButton = data.blocks[1] as SbButtonBlock;

  return (
    <PageContainer>
      <StyledLogo src={theme?.logo.white} alt="Kotipizza" />
      <StyledVerticalCenteringContainer>
        <StyledContainer>
          <StyledTitle>{textBlock.title}</StyledTitle>
          <StyledText>{textBlock.text}</StyledText>
          <Primary to={getLinkUrl()} aria-label="Päivitä sovellus">
            {linkButton.text}
          </Primary>
        </StyledContainer>
      </StyledVerticalCenteringContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${colors.darkGreen.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
`;

const StyledLogo = styled.img`
  position: absolute;
  width: 120px;
  top: 100px;
  margin-left: auto;
  margin-right: auto;
`;

const StyledVerticalCenteringContainer = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledTitle = styled(typography.HeadingBrandLarge)`
  color: ${colors.green.base};
  text-align: center;
  margin-bottom: 20px;
`;

const StyledText = styled.p`
  text-align: center;
  color: white;
  ${typography.textStyle}
  margin-bottom: 40px;
`;
